import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import Reveal from "@/components/Reveal";

export default function RelatedProperties({ testid = "related-properties" }) {
  const { t } = useLang();
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    api.get("/properties").then((r) => {
      const withImages = r.data.filter((p) => (p.cover_image || (p.images && p.images.length)));
      const shuffled = [...withImages].sort(() => Math.random() - 0.5);
      setProperties(shuffled.slice(0, 4));
    }).catch(() => {});
  }, []);

  if (!properties.length) return null;

  return (
    <section data-testid={testid} className="border-t border-maquis-deep/10 bg-ivoire">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 sm:pt-24 pb-36 sm:pb-44">
        <Reveal>
          <p className="text-center">
            <Link
              to="/locations"
              data-testid="feed-title-link"
              className="font-serif italic text-3xl sm:text-4xl lg:text-5xl text-maquis-deep transition-colors duration-300 hover:text-maquis-olive"
            >
              {t.contact.feed_slogan}
            </Link>
          </p>
        </Reveal>
        <div className="mt-20 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {properties.map((p, i) => {
            const weeklies = [p.weekly_price, ...(p.price_periods || []).filter((x) => x.per === "week").map((x) => x.price)].filter((v) => v != null && v > 0);
            const nightlies = [p.nightly_price, ...(p.price_periods || []).filter((x) => x.per === "night").map((x) => x.price)].filter((v) => v != null && v > 0);
            const minPrice = weeklies.length ? Math.min(...weeklies) : (nightlies.length ? Math.min(...nightlies) : null);
            const isNightly = !weeklies.length && nightlies.length > 0;
            return (
              <Reveal key={p.id} delay={(i % 4) * 0.06}>
                <Link
                  to={`/locations/${p.slug}`}
                  data-testid={`feed-card-${p.slug}`}
                  className="group block rounded-md bg-white p-5 shadow-[0_10px_30px_-16px_rgba(42,51,35,0.25)] transition-shadow duration-300 hover:shadow-[0_18px_44px_-18px_rgba(42,51,35,0.35)]"
                >
                  <h3 className="pb-3 font-serif italic font-semibold text-xl text-maquis-deep">{p.name}</h3>
                  <div className="overflow-hidden rounded-sm">
                    <img
                      src={p.cover_image || p.images[0]}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[16/11] w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                    />
                  </div>
                  <dl className="mt-4 space-y-1 text-xs text-maquis-olive">
                    <div className="flex gap-1">
                      <dt className="font-semibold text-maquis-deep">{t.gallery.place} :</dt>
                      <dd>{p.location.split(",")[0]}</dd>
                    </div>
                    <div className="flex gap-1">
                      <dt className="font-semibold text-maquis-deep">{t.gallery.surface} :</dt>
                      <dd>{p.surface ? `${p.surface} m²` : "—"}</dd>
                    </div>
                    <div className="flex gap-1">
                      <dt className="font-semibold text-maquis-deep">{t.gallery.people} :</dt>
                      <dd>2-{p.guests}</dd>
                    </div>
                    <div className="flex gap-1">
                      <dt className="font-semibold text-maquis-deep">{t.gallery.rooms} :</dt>
                      <dd>{p.bedrooms}</dd>
                    </div>
                  </dl>
                  {minPrice && (
                    <p data-testid={`feed-price-${p.slug}`} className="mt-3 text-sm font-semibold text-maquis-deep">
                      {t.gallery.price_from} {minPrice.toLocaleString("fr-FR")} € / {isNightly ? t.gallery.per_night : t.gallery.per_week}
                    </p>
                  )}
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
